import { css, html, LitElement } from 'lit'
import { property } from 'lit/decorators.js'
import { safeCustomElement } from '@/utilities/custom-element'
import type { HeadingAs, HeadingSize, HeadingVariant } from './types'

@safeCustomElement('mdc-heading')
export class MdcHeading extends LitElement {
  @property({ type: String }) variant: HeadingVariant = 'heading'
  @property({ type: String }) size: HeadingSize = 'md'
  @property({ type: Boolean }) italic = false
  @property({ type: String }) as: HeadingAs = 'h2'

  static styles = css`
    :host {
      display: block;
    }

    .mdc-heading {
      margin: 0;
      padding: 0;
    }

    /* Heading variant (sans-serif, 600 weight) */
    .heading {
      font-family: var(--typography-font-family-sans);
      font-weight: 600;
    }

    .heading--3xl {
      font-size: var(--typography-font-size-300);
      line-height: 1.1;
      letter-spacing: -0.03rem;
    }

    .heading--2xl {
      font-size: var(--typography-font-size-250);
      line-height: 1.1;
      letter-spacing: -0.04375rem;
    }

    .heading--xl {
      font-size: var(--typography-font-size-200);
      line-height: 1.1;
      letter-spacing: -0.02rem;
    }

    .heading--lg {
      font-size: var(--typography-font-size-175);
      line-height: 1.2;
      letter-spacing: -0.0175rem;
    }

    .heading--md {
      font-size: var(--typography-font-size-150);
      line-height: 1.3;
      letter-spacing: -0.015rem;
    }

    .heading--sm {
      font-size: var(--typography-font-size-125);
      line-height: 1.3;
      letter-spacing: -0.00625rem;
    }

    .heading--xs {
      font-size: var(--typography-font-size-112-and-half);
      line-height: 1.3;
      letter-spacing: -0.00563rem;
    }

    .heading--2xs {
      font-size: var(--typography-font-size-100);
      line-height: 1.3;
    }

    .heading--3xs {
      font-size: var(--typography-font-size-087-and-half);
      line-height: 1.3;
    }

    .heading--4xs {
      font-size: var(--typography-font-size-075);
      line-height: 1.3;
    }

    /* Display variant (serif, 400 weight) */
    .display {
      font-family: var(--typography-font-family-serif-regular);
      font-weight: 400;
      font-style: normal;
    }

    .display--italic {
      font-family: var(--typography-font-family-serif-italic);
      font-style: italic;
    }

    .display--2xl {
      font-size: var(--typography-font-size-250);
      line-height: 1.1;
    }

    .display--md {
      font-size: var(--typography-font-size-150);
      line-height: 1.2;
    }

    .display--sm {
      font-size: var(--typography-font-size-125);
      line-height: 1.2;
    }
  `

  render() {
    const tag = this.as
    const variantClass = this.variant
    const sizeClass = `${this.variant}--${this.size}`
    const italicClass = this.variant === 'display' && this.italic ? 'display--italic' : ''

    return this._renderTag(tag, `mdc-heading ${variantClass} ${sizeClass} ${italicClass}`.trim())
  }

  private _renderTag(tag: string, classes: string) {
    switch (tag) {
      case 'h1':
        return html`<h1 class=${classes}><slot></slot></h1>`
      case 'h2':
        return html`<h2 class=${classes}><slot></slot></h2>`
      case 'h3':
        return html`<h3 class=${classes}><slot></slot></h3>`
      case 'h4':
        return html`<h4 class=${classes}><slot></slot></h4>`
      case 'h5':
        return html`<h5 class=${classes}><slot></slot></h5>`
      case 'h6':
        return html`<h6 class=${classes}><slot></slot></h6>`
      default:
        return html`<h2 class=${classes}><slot></slot></h2>`
    }
  }
}
